import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Button,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import FamilyIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { getMyFamily, Family, notifyArrival } from '../api/families';
import { useAuth } from '../context/AuthProvider';
import { useSocket } from '../context/SocketProvider';
import { useStudents } from '../hooks/useStudents';
import RegisteredStudentsList from '../components/RegisteredStudentsList';
import AuthorizedPersonsList from '../components/AuthorizedPersonsList';
import ParentArrivalDialog from '../components/ParentArrivalDialog';
import DashboardSummaryCards from '../components/DashboardSummaryCards';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ParentDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const socket = useSocket();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(0);
  const [family, setFamily] = useState<Family | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noFamily, setNoFamily] = useState(false);
  const [arrivalDialogOpen, setArrivalDialogOpen] = useState(false);
  const [dismissalNotification, setDismissalNotification] = useState<string | null>(null);

  // Fetch current dismissal records for real-time status
  const { data: dismissalRecords = [] } = useStudents();

  useEffect(() => {
    loadFamily();
  }, []);

  // Listen for dismissal notifications
  useEffect(() => {
    const socketInstance = socket?.socket;
    if (!socketInstance || !user) return;

    const handleDismissalNotification = (data: any) => {
      // Check if notification is for this user
      if (data.userId === user.id) {
        const studentName = `${data.student.first_name} ${data.student.last_name}`;
        setDismissalNotification(
          `${studentName} has been dismissed and is ready for pickup!`
        );
      }
    };

    socketInstance.on('student_dismissed_notification', handleDismissalNotification);

    return () => {
      socketInstance.off('student_dismissed_notification', handleDismissalNotification);
    };
  }, [socket, user]);

  const loadFamily = async () => {
    try {
      setLoading(true);
      setError(null);
      const familyData = await getMyFamily();
      setFamily(familyData);
      setNoFamily(false);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No family found - needs to register
        setNoFamily(true);
      } else {
        setError(err.response?.data?.message || 'Failed to load family information');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleArrivalSubmit = async (studentIds: number[], pickupType: 'car' | 'foot') => {
    await notifyArrival({ student_ids: studentIds, pickup_type: pickupType });
  };

  // Calculate summary statistics
  const getSummaryStats = () => {
    if (!family) {
      return {
        totalStudents: 0,
        studentsWaiting: 0,
        studentsReady: 0,
        authorizedPersonsCount: 0,
      };
    }

    const studentsWaiting = dismissalRecords.filter(
      (record) => !record.dismissed_at
    ).length;

    const studentsReady = dismissalRecords.filter(
      (record) => record.dismissed_at
    ).length;

    return {
      totalStudents: family.students?.length || 0,
      studentsWaiting,
      studentsReady,
      authorizedPersonsCount: family.authorizedPersons?.length || 0,
    };
  };

  const summaryStats = getSummaryStats();

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (noFamily) {
    return (
      <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <FamilyIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Welcome to the Family Portal
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
            You haven't registered your family yet. Let's get started by adding your students and
            authorized pickup persons.
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/family/register')}
            sx={{
              mt: 2,
              minHeight: { xs: 48, sm: 42 },
              fontSize: { xs: '1rem', sm: '0.875rem' }
            }}
          >
            Register Your Family
          </Button>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, sm: 3, md: 4 }, mb: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, fontWeight: 600 }}>
          Family Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
          Welcome, {user?.firstName} {user?.lastName}!
        </Typography>
      </Box>

      {/* Dashboard Summary Cards */}
      <DashboardSummaryCards
        totalStudents={summaryStats.totalStudents}
        studentsWaiting={summaryStats.studentsWaiting}
        studentsReady={summaryStats.studentsReady}
        authorizedPersonsCount={summaryStats.authorizedPersonsCount}
      />

      {/* I'm Here for Pickup Button */}
      <Paper elevation={2} sx={{ mb: 3, p: { xs: 2, sm: 3 } }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, mb: 0.5 }}>
              Ready to Pick Up?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }}>
              Let staff know you've arrived for student pickup
            </Typography>
          </Box>
          <Button
            variant="pickup"
            size={isMobile ? 'large' : 'large'}
            startIcon={<DirectionsCarIcon />}
            onClick={() => setArrivalDialogOpen(true)}
            disabled={!family?.students || family.students.length === 0}
            fullWidth={isMobile}
            sx={{
              minHeight: { xs: 56, sm: 48 },
              px: { xs: 3, sm: 4 },
            }}
          >
            I'm Here for Pickup
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={3}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="family dashboard tabs"
            variant={isMobile ? 'fullWidth' : 'standard'}
            sx={{
              '& .MuiTab-root': {
                minHeight: { xs: 56, sm: 48 },
                fontSize: { xs: '0.875rem', sm: '0.9375rem' }
              }
            }}
          >
            <Tab
              icon={<SchoolIcon />}
              label="My Students"
              iconPosition={isMobile ? 'top' : 'start'}
            />
            <Tab
              icon={<PeopleIcon />}
              label="Authorized Persons"
              iconPosition={isMobile ? 'top' : 'start'}
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <RegisteredStudentsList
            students={family?.students || []}
            dismissalRecords={dismissalRecords}
            onStudentUpdated={loadFamily}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <AuthorizedPersonsList
            authorizedPersons={family?.authorizedPersons || []}
            onPersonUpdated={loadFamily}
          />
        </TabPanel>
      </Paper>

      {/* I'm Here Dialog */}
      <ParentArrivalDialog
        open={arrivalDialogOpen}
        onClose={() => setArrivalDialogOpen(false)}
        students={family?.students || []}
        onSubmit={handleArrivalSubmit}
      />

      {/* Dismissal Notification Snackbar */}
      <Snackbar
        open={!!dismissalNotification}
        autoHideDuration={10000}
        onClose={() => setDismissalNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setDismissalNotification(null)}
          severity="success"
          variant="filled"
          icon={<NotificationsActiveIcon />}
          sx={{ width: '100%' }}
        >
          {dismissalNotification}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ParentDashboardPage;
